import React, { useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Tag,
  Checkbox,
  Space,
  message,
  Spin,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  LinkOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getallENQAdmin, getSkuList, updateEnquiry } from "../services/api.js";
import Cookies from "js-cookie";
import {
  PAYMENT_OPTIONS,
  STATUS_OPTIONS,
  STATUS_COLORS,
  PAYMENT_COLORS,
  DEAL_COLORS,
  tsToDayjs,
  dayjsToTs,
  DEAL_OPTIONS,
  capitalize,
} from "../components/utils.js";

export default function AdminEnquiryById() {
  const { enqNo } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const accessToken = Cookies.get("token");

  const { data, isLoading } = useQuery({
    queryKey: ["enquiryDetails", enqNo],
    enabled: !!enqNo && !!accessToken,
    queryFn: async () => {
      const res = await getallENQAdmin(`?id=${enqNo}`, accessToken);
      return res?.enquiries?.[0] || null;
    },
  });

  const enquiry = data;

  const { data: skuData, isLoading: skuLoading } = useQuery({
    queryKey: ["sku-list"],
    queryFn: getSkuList,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  const skuOptions = useMemo(
    () =>
      (skuData?.items || []).map((item) => ({
        label: item.sku,
        value: item.sku,
      })),
    [skuData],
  );

  const skuTitleMap = useMemo(
    () =>
      (skuData?.items || []).reduce((acc, item) => {
        acc[item.sku] = item.title;
        return acc;
      }, {}),
    [skuData],
  );

  useEffect(() => {
    if (!enquiry) return;

    form.setFieldsValue({
      deal: enquiry.deal || "open",
      status: enquiry.status || "Created",
      paymentStatus: enquiry.paymentStatus || "",
      groupSize: enquiry.groupSize ?? 0,
      preferredDate: enquiry.preferredDate
        ? tsToDayjs(enquiry.preferredDate)
        : null,
      billingLink: enquiry.billingLink || "",
      paymentLink: enquiry.paymentLink || "",
      price: enquiry.price ?? "",
      knowSwimming:
        enquiry.knowSwimming === true || enquiry.knowSwimming === "Yes",
      notes: enquiry.notes || "",
      name: enquiry.name || "",
      email: enquiry.email || "",
      phoneNo: enquiry.phoneNo || "",
      sku: enquiry.sku || "",
      title: enquiry.title || "",
    });
  }, [enquiry, form]);

  const handleWhatsApp = (type) => {
    const phone = (form.getFieldValue("phoneNo") || "").replace(/\D/g, "");

    if (!phone) {
      messageApi.error("Please enter a valid phone number.");
      return;
    }

    const name = (form.getFieldValue("name") || "").split(" ")[0] || "there";
    const title = form.getFieldValue("title") || "your enquiry";

    const link =
      type === "invoice"
        ? form.getFieldValue("billingLink")
        : form.getFieldValue("paymentLink");

    if (!link) {
      messageApi.error(
        type === "invoice"
          ? "Invoice link is empty."
          : "Payment link is empty.",
      );
      return;
    }

    const text =
      type === "invoice"
        ? `Hi ${name}, here is your invoice for ${title}:\n${link}\n\nPlease review the invoice and let us know if you have any questions.`
        : `Hi ${name}, here is your payment link for ${title}:\n${link}\n\nPlease let us know once the payment is completed.`;

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateEnquiry(id, updates, accessToken),
    onSuccess: () => {
      messageApi.success("Enquiry updated successfully.");
      queryClient.invalidateQueries({ queryKey: ["allENQ"] });
      queryClient.invalidateQueries({
        queryKey: ["enquiryDetails", enqNo],
      });
    },
    onError: (error) => {
      messageApi.error(error?.message || "Failed to update enquiry.");
    },
  });

  const handleOpenLink = (url) => {
    if (!url) {
      messageApi.warning("No link available.");
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleUpdate = async () => {
    if (!enquiry) return;

    let values;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const updates = {
      deal: values.deal,
      status: values.status,
      paymentStatus: values.paymentStatus || "",
      groupSize: values.groupSize ?? 0,
      preferredDate: values.preferredDate
        ? dayjsToTs(values.preferredDate)
        : null,
      billingLink: (values.billingLink || "").trim(),
      paymentLink: (values.paymentLink || "").trim(),
      price: values.price ?? "",
      knowSwimming: values.knowSwimming ? "Yes" : "",
      notes: values.notes || "",
      name: (values.name || "").trim(),
      email: (values.email || "").trim(),
      phoneNo: (values.phoneNo || "").trim(),
      sku: values.sku || "",
      title: values.title || "",
    };

    if (
      updates.status === "Invoiced" &&
      (!updates.billingLink || !updates.paymentLink)
    ) {
      messageApi.error(
        "Add both the invoice link and payment link before marking this enquiry as Invoiced.",
      );
      return;
    }

    if (
      updates.status === "Service Provided" &&
      updates.paymentStatus !== "Paid"
    ) {
      messageApi.error(
        "Mark the payment status as Paid before marking the service as provided.",
      );
      return;
    }

    const changedFields = {};

    Object.keys(updates).forEach((key) => {
      const oldValue = enquiry[key] ?? "";
      const newValue = updates[key] ?? "";

      if (key === "preferredDate") {
        const oldDate = oldValue?.seconds ? oldValue.seconds : oldValue;
        const newDate = newValue?.seconds ? newValue.seconds : newValue;

        if (oldDate !== newDate) {
          changedFields[key] = newValue;
        }
      } else if (String(oldValue) !== String(newValue)) {
        changedFields[key] = newValue;
      }
    });

    if (Object.keys(changedFields).length === 0) {
      messageApi.info("No changes to update.");
      return;
    }

    updateMutation.mutate({
      id: enquiry.id || enquiry.enqNo,
      updates: changedFields,
    });
  };

  if (isLoading) {
    return (
      <Row justify="center" style={{ padding: 40 }}>
        <Spin />
      </Row>
    );
  }

  if (!enquiry) {
    return <Empty description="Enquiry not found or access denied." />;
  }

  return (
    <>
      {contextHolder}

      <Row
        justify="center"
        style={{
          minHeight: "100vh",
          background: "#f5f5f5",
          padding: 16,
        }}
      >
        <Col xs={24} md={22} lg={18}>
          <Card
            title={
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate("/admin/enquiries")}
                />
                <span>{enquiry.enqNo}</span>
              </Space>
            }
            styles={{
              body: { padding: 16 },
            }}
          >
            <Form
              form={form}
              layout="vertical"
              size="middle"
              disabled={updateMutation.isPending}
            >
              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Card size="small" title="Customer & enquiry">
                    <Form.Item label="Enquiry ID">
                      <Input value={enquiry.enqNo} disabled />
                    </Form.Item>

                    <Form.Item name="title" hidden>
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Service / SKU"
                      name="sku"
                      rules={[
                        {
                          required: true,
                          message: "Please select a service.",
                        },
                      ]}
                    >
                      <Select
                        loading={skuLoading}
                        options={skuOptions}
                        showSearch
                        optionFilterProp="label"
                        onChange={(sku) => {
                          if (skuTitleMap[sku]) {
                            form.setFieldsValue({
                              title: skuTitleMap[sku],
                            });
                          }
                        }}
                      />
                    </Form.Item>

                    <Form.Item label="Customer name" name="name">
                      <Input placeholder="Customer name" />
                    </Form.Item>

                    <Form.Item label="Email" name="email">
                      <Input placeholder="Email (optional)" />
                    </Form.Item>

                    <Form.Item label="WhatsApp number" name="phoneNo">
                      <Input placeholder="Phone number with country code" />
                    </Form.Item>

                    <Form.Item label="Group size" name="groupSize">
                      <InputNumber
                        min={0}
                        max={999}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>

                    <Form.Item label="Preferred date" name="preferredDate">
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD-MM-YYYY"
                      />
                    </Form.Item>

                    <Form.Item name="knowSwimming" valuePropName="checked">
                      <Checkbox>Customer knows swimming</Checkbox>
                    </Form.Item>

                    <Form.Item label="Internal notes" name="notes">
                      <Input.TextArea
                        rows={4}
                        placeholder="Add internal notes..."
                      />
                    </Form.Item>
                  </Card>
                </Col>

                <Col xs={24} md={10}>
                  <Card size="small" title="Deal & payment">
                    <Form.Item label="Deal" name="deal">
                      <Select>
                        {DEAL_OPTIONS.map((option) => (
                          <Select.Option key={option} value={option}>
                            <Tag color={DEAL_COLORS[option] || "default"}>
                              {capitalize(option)}
                            </Tag>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="Enquiry status" name="status">
                      <Select>
                        {STATUS_OPTIONS.map((option) => (
                          <Select.Option key={option} value={option}>
                            <Tag color={STATUS_COLORS[option] || "default"}>
                              {option}
                            </Tag>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="Payment status" name="paymentStatus">
                      <Select>
                        {PAYMENT_OPTIONS.map((option) => (
                          <Select.Option key={option || "empty"} value={option}>
                            <Tag
                              color={
                                PAYMENT_COLORS[option || "Null"] || "default"
                              }
                            >
                              {option || "Not set"}
                            </Tag>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="Price" name="price">
                      <Input placeholder="Total price (₹)" />
                    </Form.Item>

                    <Form.Item label="Invoice link" name="billingLink">
                      <Input
                        placeholder="Paste invoice URL"
                        addonAfter={
                          <Space size={2}>
                            <Button
                              type="text"
                              icon={<LinkOutlined />}
                              onClick={() =>
                                handleOpenLink(
                                  form.getFieldValue("billingLink"),
                                )
                              }
                            />
                            <Button
                              type="text"
                              icon={
                                <WhatsAppOutlined
                                  style={{ color: "#25D366" }}
                                />
                              }
                              onClick={() => handleWhatsApp("invoice")}
                            />
                          </Space>
                        }
                      />
                    </Form.Item>

                    <Form.Item label="Payment link" name="paymentLink">
                      <Input
                        placeholder="Paste payment URL"
                        addonAfter={
                          <Space size={2}>
                            <Button
                              type="text"
                              icon={<LinkOutlined />}
                              onClick={() =>
                                handleOpenLink(
                                  form.getFieldValue("paymentLink"),
                                )
                              }
                            />
                            <Button
                              type="text"
                              icon={
                                <WhatsAppOutlined
                                  style={{ color: "#25D366" }}
                                />
                              }
                              onClick={() => handleWhatsApp("payment")}
                            />
                          </Space>
                        }
                      />
                    </Form.Item>
                  </Card>
                </Col>
              </Row>

              <Row justify="end" style={{ marginTop: 16 }}>
                <Space>
                  <Button onClick={() => navigate("/admin/enquiries")}>
                    Cancel
                  </Button>

                  <Button
                    type="primary"
                    onClick={handleUpdate}
                    loading={updateMutation.isPending}
                  >
                    Save changes
                  </Button>
                </Space>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
